import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styles from './Body.module.css';
import CampaignOverview from '../CampaignOverview/CampaignOverview';
import DeliveryReport from '../DeliveryReport/DeliveryReport';
import HashParameterService from '../../../services/hashParameterService';
import { Lp5ConfigurationProvider } from '../Contexts/Lp5ConfigurationContext';
import campaignValues from '../../../utils/campaignValues';

const useOrderlineData = (orderData, hashParamService, setHeaderDR, setForceUsePop, setSelectedOrderlineMetaData) => {
    const [dataType, setDataType] = useState("");
    const [orderlinesData, setOrderlinesData] = useState(null);
    const [campaignType, setCampaignType] = useState("");
    const [campaignPromiseType, setCampaignPromiseType] = useState("");
    const [selectedOrderlineIds, setSelectedOrderlineIds] = useState([]);
    const [lp5, setLp5] = useState("");

    const handleLoadOrderline = useCallback((orderlineIdArray) => {
        if (orderlineIdArray === null || orderlineIdArray.length === 0) {
            return;
        }
        setHeaderDR(true);
        const filteredOrderLines = orderData.orderLines.filter(orderline =>
            orderlineIdArray.includes(orderline.id)
        );
        if (false) { // add condition to use Pop instead of POD here
            setForceUsePop(true);
        }
        if (filteredOrderLines.length > 0) {
            const camType = filteredOrderLines[0].type;
            //console.log('campaign type ', camType);
            setCampaignType(camType);
            const localParameter5 = typeof filteredOrderLines[0].localParameter5 === "string"
                ? filteredOrderLines[0].localParameter5.toLowerCase()
                : "";
            const campaignPromise = campaignValues.fetchLocalParam5CampaignPromise(localParameter5);
            setCampaignPromiseType(campaignPromise);
            //console.log('Promise type ', campaignPromise);
            setSelectedOrderlineMetaData(filteredOrderLines[0]);
            setLp5(localParameter5);
        }
        // Set orderlines in URL
        hashParamService.updateParameter('orderlines', orderlineIdArray.join(','));
        setSelectedOrderlineIds(orderlineIdArray);
    }, [orderData.orderLines, setHeaderDR, setForceUsePop, hashParamService]);

    useEffect(() => {
        const orderlinesFromUrl = hashParamService.getParameter('orderlines');
        if (orderlinesFromUrl !== null && orderlinesFromUrl !== "") {
            const selectedIds = orderlinesFromUrl.split(',')
                .filter(orderline => orderline.trim() !== '')
                .map(String);
            handleLoadOrderline(selectedIds);
        }
    }, [hashParamService, handleLoadOrderline]);

    return {
        dataType,
        setDataType,
        orderlinesData,
        setOrderlinesData,
        campaignType,
        campaignPromiseType,
        lp5,
        handleLoadOrderline
    };
};

const Body = ({
    orderData,
    customerId,
    parameters,
    activePage,
    setActivePage,
    setHeaderDR,
    setForceUsePop,
    setSelectedOrderlineMetaData,
    campaignName
}) => {
    const hashParamService = useMemo(() => new HashParameterService(), []);

    const {
        dataType,
        setDataType,
        orderlinesData,
        setOrderlinesData,
        campaignType,
        campaignPromiseType,
        lp5,
        handleLoadOrderline,
    } = useOrderlineData(
        orderData,
        hashParamService,
        setHeaderDR,
        setForceUsePop,
        setSelectedOrderlineMetaData
    );

    const orderlinesFromUrl = hashParamService.getParameter('orderlines');

    return (
        <main className={styles.body}>
            <Lp5ConfigurationProvider lp5={lp5}>
                {orderlinesFromUrl === null || orderlinesFromUrl === "" ? (
                    <CampaignOverview
                        orderData={orderData}
                        customerId={customerId}
                        onLoadOrderline={handleLoadOrderline}
                        setForceUsePop={setForceUsePop}
                        parameters={parameters}
                    />
                ) : (
                    <DeliveryReport
                        orderData={orderData}
                        customerId={customerId}
                        parameters={parameters}
                        dataType={dataType}
                        campaignType={campaignType}
                        activePage={activePage}
                        setActivePage={setActivePage}
                        orderlinesData={orderlinesData}
                        setOrderlinesData={setOrderlinesData}
                        campaignPromiseType={campaignPromiseType}
                        campaignName={campaignName}
                    />
                )}
            </Lp5ConfigurationProvider>
        </main>
    );
};

Body.propTypes = {
    orderData: PropTypes.shape({
        orderLines: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
            localParameter5: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })).isRequired
    }).isRequired,
    customerId: PropTypes.string.isRequired,
    parameters: PropTypes.object.isRequired,
    activePage: PropTypes.string.isRequired,
    setActivePage: PropTypes.func.isRequired,
    setHeaderDR: PropTypes.func.isRequired,
    setForceUsePop: PropTypes.func.isRequired,
    setSelectedOrderlineMetaData: PropTypes.func.isRequired,
    campaignName: PropTypes.string.isRequired
};

export default Body;